import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  isMobileImmersiveChromeRoute,
  isPostureExtendedChromeRoute,
  isSopExtendedChromeRoute,
  MOBILE_PAGE_HEADER_COLLAPSED_HEIGHT_PX,
} from '@/lib/mobile-immersive-chrome';

export { MOBILE_PAGE_HEADER_COLLAPSED_HEIGHT_PX };

export interface PageHeaderVisibility {
  /** True when mobile chrome is dismissed (immersive mode). */
  isChromeHidden: boolean;
  /** Alias for {@link isChromeHidden} on immersive routes. */
  isHeaderHidden: boolean;
  /** Immersive toggle is active on this route (mobile + not dashboard/chat). */
  isImmersiveChromeEnabled: boolean;
  /** Posture page: also dismiss toolbar, view tabs, analysis panel chrome. */
  isPostureExtendedChrome: boolean;
  /** SOP assessment: also dismiss mobile action button rows above bottom nav. */
  isSopExtendedChrome: boolean;
  /** Posture mobile split: view tabs + analysis segment visible above bottom nav. */
  postureBottomChromeStackActive: boolean;
  toggleChrome: () => void;
  setChromeHidden: (hidden: boolean) => void;
  setPostureBottomChromeStackActive: (active: boolean) => void;
}

const noop = () => {};

const defaultValue: PageHeaderVisibility = {
  isChromeHidden: false,
  isHeaderHidden: false,
  isImmersiveChromeEnabled: false,
  isPostureExtendedChrome: false,
  isSopExtendedChrome: false,
  postureBottomChromeStackActive: false,
  toggleChrome: noop,
  setChromeHidden: noop,
  setPostureBottomChromeStackActive: noop,
};

export const PageHeaderVisibilityContext = createContext<PageHeaderVisibility>(defaultValue);

export function usePageHeaderVisibility(): PageHeaderVisibility {
  return useContext(PageHeaderVisibilityContext);
}

export interface MobileChromeVisibilityProviderProps {
  children: ReactNode;
  /** When false, provider passes through defaults (login layout). */
  enabled: boolean;
}

/**
 * Supplies immersive chrome state for mobile capture routes (posture, SOP).
 */
export function MobileChromeVisibilityProvider({
  children,
  enabled,
}: MobileChromeVisibilityProviderProps) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isChromeHidden, setChromeHiddenState] = useState(false);
  const [postureBottomChromeStackActive, setPostureBottomChromeStackActiveState] = useState(false);

  const isImmersiveChromeEnabled =
    enabled && isMobile && isMobileImmersiveChromeRoute(location.pathname);
  const isPostureExtendedChrome =
    isImmersiveChromeEnabled && isPostureExtendedChromeRoute(location.pathname);
  const isSopExtendedChrome =
    isImmersiveChromeEnabled && isSopExtendedChromeRoute(location.pathname);

  const toggleChrome = useCallback(() => {
    setChromeHiddenState((v) => !v);
  }, []);

  const setChromeHidden = useCallback((hidden: boolean) => {
    setChromeHiddenState(hidden);
  }, []);

  const setPostureBottomChromeStackActive = useCallback((active: boolean) => {
    setPostureBottomChromeStackActiveState(active);
  }, []);

  useEffect(() => {
    setChromeHiddenState(false);
    setPostureBottomChromeStackActiveState(false);
  }, [location.pathname]);

  const value = useMemo((): PageHeaderVisibility => {
    if (!isImmersiveChromeEnabled) {
      return defaultValue;
    }
    return {
      isChromeHidden,
      isHeaderHidden: isChromeHidden,
      isImmersiveChromeEnabled: true,
      isPostureExtendedChrome,
      isSopExtendedChrome,
      postureBottomChromeStackActive,
      toggleChrome,
      setChromeHidden,
      setPostureBottomChromeStackActive,
    };
  }, [
    isImmersiveChromeEnabled,
    isPostureExtendedChrome,
    isSopExtendedChrome,
    postureBottomChromeStackActive,
    isChromeHidden,
    toggleChrome,
    setChromeHidden,
    setPostureBottomChromeStackActive,
  ]);

  return (
    <PageHeaderVisibilityContext.Provider value={value}>
      {children}
    </PageHeaderVisibilityContext.Provider>
  );
}
